/**
 * Builds a map of traveller (employee/guest) IDs to their assigned room indices
 * from accommodation items that contain occupancy information.
 *
 * @param accommodationItems - Array of accommodation items with occupancy data
 * @returns Map where keys are traveller IDs and values are room indices (0-based)
 */
export function buildTravellerToRoomMap(
  accommodationItems: Array<App.Checkout.LEHotelItem | App.Checkout.BedbankHotelItem>,
): Map<string, number> {
  const travellerToRoomMap = new Map<string, number>()

  accommodationItems.forEach((item, roomIndex) => {
    if ('occupancy' in item && item.occupancy) {
      item.occupancy.employeeIds?.forEach((empId: string) => {
        travellerToRoomMap.set(empId, roomIndex)
      })
      item.occupancy.guestIds?.forEach((guestId: string) => {
        travellerToRoomMap.set(guestId, roomIndex)
      })
    }
  })

  return travellerToRoomMap
}

/**
 * Gets the room index for a specific traveller (employee or guest).
 *
 * @param travellerId - The employee ID or guest ID to look up
 * @param accommodationItemsOrMap - Either an array of accommodation items with occupancy data, or a pre-built map of traveller IDs to room indices
 * @returns The room index (0-based) if found, undefined otherwise
 */
export function getTravellerRoomIndex(
  travellerId: string,
  accommodationItemsOrMap: Array<App.Checkout.LEHotelItem | App.Checkout.BedbankHotelItem> | Map<string, number>,
): number | undefined {
  const travellerToRoomMap = accommodationItemsOrMap instanceof Map
    ? accommodationItemsOrMap
    : buildTravellerToRoomMap(accommodationItemsOrMap)
  return travellerToRoomMap.get(travellerId)
}
